import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePricingAdminUser';
import { FilterablePayoutTransactionTable } from '@/components/Payouts/FilterablePayoutTransactionTable';
import { PayoutTransactionFilterName } from '@/components/Payouts/PayoutTransactionsFilter';
import { PayoutTransactionTableColumnName } from '@/components/Payouts/PayoutTransactionTable';
import { ProjectTransactionButton } from '@/components/Payouts/ProjectTransactionsButtonBar';
import { useGetBatchGroupsQuery } from '@/services/payouts';
import { Box, Card, toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import capitalize from 'lodash/capitalize';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BatchReviewStatus, IsoDateString, BatchType, ProjectTransactionSort, UserPermissions } from 'types';

export const ProjectTransactionsView = () => {
  const { batchDate } = useParams<{ batchDate: IsoDateString }>();
  const [searchParams] = useSearchParams();
  const batchType = searchParams.get('batchType') as BatchType;
  const { claims } = useAuth();
  const navigate = useNavigate();
  const { enableAdhocBatches = false } = useFlags();
  const { data: batches, isFetching: isFetchingBatches } = useGetBatchGroupsQuery(
    batchDate ? { batchDate, batchType } : skipToken,
  );
  const batchCanBeApproved = useMemo(
    () => batches?.some((batch) => batch?.batchReviewStatus !== BatchReviewStatus.approved),
    [batches],
  );

  // Moving transactions out invalidates the Batches list, so this query refetches on its own. An
  // empty result means every transaction left and the backend soft-deleted the batch — there is no
  // longer a page to look at.
  const hasReportedEmptyBatchRef = useRef(false);
  useEffect(() => {
    if (isFetchingBatches || !batches || batches.length > 0 || hasReportedEmptyBatchRef.current) return;
    hasReportedEmptyBatchRef.current = true;
    toast.success('This batch is now empty and has been removed.');
    // `replace`, not push: pushing leaves the drained batch in history, so Back returns to a page
    // that remounts, resets the ref, finds the batch still empty and bounces forward again.
    navigate('/admin/payouts/queued', { replace: true });
  }, [batches, isFetchingBatches, navigate]);

  const isPalmettoFinanceReaderOrAdmin = useMemo(() => {
    return (
      isPalmettoFinanceUser(claims) &&
      (claims?.permissions?.includes(UserPermissions.lightReachAccountingAdmin) ||
        claims?.permissions?.includes(UserPermissions.lightReachAccountingReader))
    );
  }, [claims]);

  const hideColumns = useMemo(() => {
    const columns: PayoutTransactionTableColumnName[] = [
      'batch.batchDate',
      'projectExternalId',
      'pauseExpirationDate',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('payee.name');
      columns.push('tags'); // manual or on hold tags
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideFilters = useMemo(() => {
    const columns: PayoutTransactionFilterName[] = [
      'eventDate',
      'payoutHold',
      'batchDate',
      'pauseExpirationDate',
      'pausedDueToPaymentPlan',
      'payeeType',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      columns.push('payeeName');
      columns.push('isManual');
      columns.push('clawbackRuleName');
    }
    return columns;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const hideSort = useMemo(() => {
    const sorts: ProjectTransactionSort[] = [
      'pausedExpirationDateAsc',
      'pausedExpirationDateDesc',
      'batchDateAsc',
      'batchDateDesc',
    ];
    if (!isPalmettoFinanceReaderOrAdmin) {
      sorts.push('payeeNameAsc', 'payeeNameDesc');
    }
    return sorts;
  }, [isPalmettoFinanceReaderOrAdmin]);

  const batchDateDisplay = useMemo(() => {
    return batchDate ? DateTime.fromISO(batchDate).toFormat('EEEE MMM d, yyyy') : '';
  }, [batchDate]);

  const filters = useMemo(
    () => ({
      batchDate,
      batchType,
    }),
    [batchDate, batchType],
  );

  const buttons = useMemo(() => {
    let buttons: ProjectTransactionButton[] = ['export'];
    if (batchCanBeApproved) {
      buttons = [...buttons, 'addTransaction', 'approve', 'recalculate', 'unbatch'];
      // Only with the flag on: the legacy add-to-batch handler rejects transactions that are
      // already batched, which is every transaction on this page.
      if (enableAdhocBatches) {
        buttons = [...buttons, 'addToBatch'];
      }
    }
    return buttons;
  }, [batchCanBeApproved, enableAdhocBatches]);

  return (
    <>
      <Helmet>
        <title>Payouts</title>
      </Helmet>
      <Card padding="sm" marginTop="0">
        <Card.Header>
          <Box direction="row" justifyContent="space-between">
            <Box fontSize="xl" fontWeight="medium">
              {capitalize(batchType)} Payouts - {batchDateDisplay}
            </Box>
          </Box>
        </Card.Header>
        <Box margin="0 lg 0 lg">
          <FilterablePayoutTransactionTable
            lockedQueryFilters={filters}
            enabledButtons={buttons}
            hideColumns={hideColumns}
            hideFilters={hideFilters}
            hideSort={hideSort}
          />
        </Box>
      </Card>
    </>
  );
};
